/* dynamic-form.component.ts */
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { map, Observable } from 'rxjs';

// Angular Material & other imports...
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';

import { FormConfigService } from '../forms-config.service';
import { AppStateService } from '../../app-state.service';
import { DeeplinkService } from '../../deeplink.service';
import { ConsentPromptComponent } from '../consent-prompt/consent-prompt.component';
import { MaterialModule } from '../../material.module';
import { AddressAutocompleteDirective } from '../../address-auto-complete.directive';
import { AutoSlashDateFormatDirective } from '../../auto-slash-date-format.directive';
import { AutoDashSSNFormatDirective } from '../../auto-ssn-format.directive';
import { SignaturePadComponent } from '../signature-pad/signature-pad.component';
import { AddressAutoCompleteComponent } from '../address-auto-complete/address-auto-complete.component';
import { MarkdownComponent } from '../markdown/markdown.component';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatButtonModule } from '@angular/material/button';
import { AppointmentService } from '../appointment.service';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    SignaturePadComponent,
    AutoDashSSNFormatDirective,
    AddressAutoCompleteComponent,
    MarkdownComponent
],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true }
    }
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit {
  // So we can access stepper programmatically if needed
  @ViewChild('stepper') stepper!: MatStepper;

  appStateService = inject(AppStateService);
  formConfigService = inject(FormConfigService);
  snackBar = inject(MatSnackBar);
  deeplinkService = inject(DeeplinkService);
  appointmentService = inject(AppointmentService);

  @Input() clientId: string = '';
  @Input() patientId: string = '';
  @Input() templateId: string = '';
  @Input() kioskMode: boolean = false;
  @Input() patientData: any = null;  // Patient data for pre-filling form (used in kiosk mode)
  @Output() formSubmitted = new EventEmitter<{ success: boolean; formId: string; templateId: string }>();

  // Each page => FormGroup
  formPages: { [key: string]: FormGroup } = {};
  // The form JSON structure
  formConfig: any = {};
  formTemplate: any = {};
  layout: any = { pages: [] };

  // Track if the stepper is linear
  isLinear = true;
  // Each step's "saved" state
  stepIsSaved: boolean[] = [];

  // Firestore form instance ID
  formId: string = '';
  // Potential filtering of form templates
  filteredFormTemplates: any[] = [];

  loading = false;
  loadingSection = false;
  // Keep track of file input names for duplicates
  fileNames: { [key: string]: { [key: string]: string } } = {};

  stepperOrientation: Observable<any>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    breakpointObserver: BreakpointObserver,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || sessionStorage.getItem('patientId') || '';

    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 600px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnInit(): void {
    
    if (this.patientId) {
      this.loadPatientData();
    } else {
      this.showError('Patient ID not found. Please try again.', true);
    }
  }

  // Intercept step selection changes (header clicks, manual changes)
  onStepSelectionChange(event: StepperSelectionEvent): void {
    const prevIndex = event.previouslySelectedIndex;
    const nextIndex = event.selectedIndex;

    // If the previous step is not saved, revert
    if (!this.stepIsSaved[prevIndex]) {
      // Move user back to the previous step
      // this.stepper.selectedIndex = prevIndex;
      this.showError('Please save this step before proceeding.');
    }
    // If saved, we allow the navigation
  }

  sanitizeUrl(url: string): SafeUrl | null {
    try {
      return this.sanitizer.bypassSecurityTrustUrl(url);
    } catch (error) {
      return null;
    }
  }

  loadPatientData(): void {
    if (!this.clientId || !this.patientId) {
      return;
    }
    this.loading = true;

    // In kiosk mode, use provided patient data instead of fetching
    if (this.kioskMode) {
      // Convert kiosk patient data format to match form field names
      const kioskPatientData = this.patientData ? {
        firstName: this.patientData.firstName,
        lastName: this.patientData.lastName,
        dateOfBirth: this.patientData.dateOfBirth,
        phoneNumber: this.patientData.phoneNumber,
        email: this.patientData.email
      } : {};
      this.fetchFormTemplate(kioskPatientData);
      return;
    }

    this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe({
      next: (data) => {
        this.fetchFormTemplate(data);
      },
      error: (err) => {
        this.loading = false;
      }
    });
  }

  fetchFormTemplate(patientData: any): void {
    // Use anonymous method for kiosk mode
    const templateObservable = this.kioskMode
      ? this.formConfigService.getFormTemplatesAnonymous(this.clientId, this.templateId)
      : this.formConfigService.getFormTemplates(this.clientId, this.templateId);

    templateObservable.subscribe({
      next: (template: any) => {
        this.formTemplate = template;
        this.formConfig = template.sections || {};
        this.layout = template.layout || { pages: [] };

        // Debug: Log the form template structure to understand backend configuration
        if (this.formConfig) {
          Object.keys(this.formConfig).forEach(sectionKey => {
            const section = this.formConfig[sectionKey];
            if (section.fields) {
              const stateField = section.fields.find((field: any) => field.name === 'state');
              if (stateField) {
              }
            }
          });
        }

        this.createForms();
        this.patchFormValues(patientData);
        this.filteredFormTemplates = [{ form_title: template.title }];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showError('Error fetching form templates.');
      }
    });
  }

  createForms(): void {
    if (Object.keys(this.formConfig).length === 0 || this.layout.pages.length === 0) {
      return;
    }

    // Initialize our stepIsSaved array
    this.stepIsSaved = new Array(this.layout.pages.length + 1).fill(false);
    // last one is review set to true
    this.stepIsSaved[this.layout.pages.length] = true;

    this.layout.pages.forEach((page: any, index: number) => {
      const formGroup = this.fb.group({});

      // For each section
      page.sections.forEach((sectionId: string) => {
        const sectionGroup = this.fb.group({});
        const section = this.formConfig[sectionId];
        if (!section) {
          return;
        }
        // Build controls
        section.fields.forEach((field: any) => {
          const validators = [];
          if (field.required) {
            validators.push(Validators.required);
          }
          if (field.validatorPattern) {
            validators.push(Validators.pattern(field.validatorPattern));
          }

          const value = field.value || field.default || '';
          const isDisabled = field.readonly || false;
          let control: FormControl | undefined;

          switch (field.type) {
            case 'email':
              validators.push(Validators.email);
              control = this.fb.control({ value, disabled: isDisabled }, validators);
              break;
            case 'checkbox-group': {
              // Create a subgroup for all options of this checkbox group
              const checkboxGroup = this.fb.group({});
              field.options.forEach((option: any) => {
                checkboxGroup.addControl(option.value, this.fb.control(false));
              });
              // Attach it to the section group keyed by field.name
              sectionGroup.addControl(field.name, checkboxGroup);
              break;
            }
            case 'signature':
            case 'text':
            case 'static_text':
              control = this.fb.control({ value, disabled: isDisabled }, validators);
              break;
            case 'date':
              const dateValue = value ? new Date(value) : '';
              control = this.fb.control({ value: dateValue, disabled: isDisabled }, validators);
              break;
            case 'number':
            case 'radio-group':
            case 'select':
            case 'textarea':
            case 'time':
            case 'file':
              control = this.fb.control({ value, disabled: isDisabled }, validators);
              break;
            default:
              // fallback
              control = this.fb.control({ value, disabled: isDisabled }, validators);
          }

          if (control) {
            sectionGroup.addControl(field.name, control);
            // Additional logic for relationshipToPatient
            if (field.name === 'relationshipToPatient') {
              control.valueChanges.subscribe((val: string) => this.onRelationshipChange(val));
            }
          }
        }); // end fields

        formGroup.addControl(sectionId, sectionGroup);
      }); // end sections

      // If user changes anything, mark this step as unsaved
      formGroup.valueChanges.subscribe(() => {
        this.stepIsSaved[index] = false;
      });

      this.formPages[page.title] = formGroup;
    });
  }

  onRelationshipChange(value: string): void {
    const medicalHistoryForm = this.formPages['Medical History']?.get('medical_history') as FormGroup;
    if (!medicalHistoryForm) return;

    const nameIfNotPatientControl = medicalHistoryForm.get('nameIfNotPatient') as FormControl;
    if (!nameIfNotPatientControl) return;

    if (value === 'Self') {
      nameIfNotPatientControl.setValue('');
      nameIfNotPatientControl.clearValidators();
      nameIfNotPatientControl.disable();
    } else {
      nameIfNotPatientControl.setValidators([Validators.required]);
      nameIfNotPatientControl.enable();
    }
    nameIfNotPatientControl.updateValueAndValidity();
  }

  patchFormValues(patientData: any): void {
    if (!patientData || !this.formConfig) {
      return;
    }

    for (const page of this.layout.pages) {
      const formGroup = this.formPages[page.title];
      if (formGroup) {
        page.sections.forEach((sectionId: string) => {
          const sectionGroup = formGroup.get(sectionId) as FormGroup;
          if (!sectionGroup) return;

          const section = this.formConfig[sectionId];
          if (section && section.fields) {
            section.fields.forEach((field: any) => {
              if (patientData[field.name] !== undefined) {
                let value = patientData[field.name];
                if (
                  field.name === 'phoneNumber' ||
                  field.name === 'homePhone' ||
                  field.name === 'emergencyPhone'
                ) {
                  value = this.formatPhoneNumber(value);
                } else if (field.name === 'dateOfBirth') {
                  value = this.adjustDateForTimezone(new Date(value));
                }
                sectionGroup.get(field.name)?.patchValue(value);
                if (field.name === 'relationshipToPatient') {
                  this.onRelationshipChange(value);
                }
              }
            });
          }
        });
      }
    }
  }

  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    return phoneNumber; // Return as-is if not strictly 10 or 11 digits
  }

  adjustDateForTimezone(date: Date): Date {
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  }

  preprocessPhoneNumber(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormControl && control.validator) {
        const validators = control.validator({} as AbstractControl);
        if (validators && validators['pattern']) {
          if (validators['pattern'].requiredPattern.includes('^\\+1\\d{10}$')) {
            control.setValue(this.formatPhoneNumber(control.value));
          }
        }
      } else if (control instanceof FormGroup) {
        this.preprocessPhoneNumber(control);
      }
    });
  }

  // Called when user clicks the "Next" button on a step
  savePage(pageTitle: string, stepper: MatStepper, stepIndex: number): void {
    const formGroup = this.formPages[pageTitle];
    if (formGroup.valid) {
      this.loadingSection = true;
      this.preprocessPhoneNumber(formGroup);

      if (!this.formId) {
        // Create a Firestore form instance first
        this.createFormInstance(() => {
          this.savePageData(pageTitle, stepper, stepIndex);
        });
      } else {
        this.savePageData(pageTitle, stepper, stepIndex);
      }
    } else {
      this.showError('Please fill all required fields before proceeding.');
      this.markAllFieldsAsTouched(formGroup);
    }
  }

  createFormInstance(callback: () => void): void {
    // Use anonymous method for kiosk mode
    const createObservable = this.kioskMode
      ? this.formConfigService.createFormInstanceAnonymous(this.clientId, this.patientId, this.templateId)
      : this.formConfigService.createFormInstance(this.clientId, this.patientId, this.templateId);

    createObservable.subscribe({
      next: (response: any) => {
        this.formId = response.form_id;
        callback();
      },
      error: () => {
        this.showError('Error creating form instance.');
      }
    });
  }

  savePageData(pageTitle: string, stepper: MatStepper, stepIndex: number): void {
    const pageSections = this.layout.pages.find((p: any) => p.title === pageTitle).sections;
    const pageData: { [sectionId: string]: any } = {};

    pageSections.forEach((sectionId: string) => {
      const sectionGroup = this.formPages[pageTitle].get(sectionId) as FormGroup;
      const sectionData: { [key: string]: any } = sectionGroup.getRawValue();

      Object.keys(sectionData).forEach((fieldName) => {
        const field = this.formConfig[sectionId]?.fields.find((f: any) => f.name === fieldName);

        // For checkbox-group, store as an array
        if (field?.type === 'checkbox-group') {
          // Convert { highBloodPressure: true, heartAttack: false } => ["High Blood Pressure"]
          // sectionData[fieldName] = Object.keys(sectionData[fieldName])
          //   .filter((key) => sectionData[fieldName][key])
          // .map((key) => field.options.find((o: any) => o.value === key)?.label);
        } else if (sectionData[fieldName] instanceof Date) {
          // Format date using local timezone to avoid timezone offset issues
          const date = sectionData[fieldName];
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          sectionData[fieldName] = `${year}-${month}-${day}`;
        }
      });

      pageData[sectionId] = sectionData;
    });

    const payload = {
      form_id: this.formId,
      template_id: this.templateId,
      patient_id: this.patientId,
      client_id: this.clientId,
      sections: pageData,
      modified_at: new Date().toISOString(),
      modified_by: 'user'
    };

    // Use anonymous method for kiosk mode
    const saveObservable = this.kioskMode
      ? this.formConfigService.saveOnboardFormAnonymous(this.clientId, this.patientId, payload)
      : this.formConfigService.saveOnboardForm(this.clientId, this.patientId, payload);

    saveObservable.subscribe({
      next: () => {
        this.clearUploadedImages(pageTitle, pageSections);
        this.loadingSection = false;
        // Mark step as saved:
        this.stepIsSaved[stepIndex] = true;
        // Move to next step
        stepper.next();
      },
      error: () => {
        this.loadingSection = false;
        this.showError(`Error saving page ${pageTitle}.`);
      }
    });
  }

  clearUploadedImages(pageTitle: string, pageSections: string[]): void {
    let clearedFiles = false;
    pageSections.forEach((sectionId: string) => {
      const sectionGroup = this.formPages[pageTitle].get(sectionId) as FormGroup;
      const fields = this.formConfig[sectionId]?.fields;

      fields?.forEach((field: any) => {
        if (field.type === 'file' && sectionGroup.get(field.name)?.value) {
          // Clear the file input for that field
          sectionGroup.get(field.name)?.setValue(null);
          clearedFiles = true;
        }
      });
    });
  }

  // Collects an entire step's checkboxes to display them, if needed
  getSelectedCheckboxGroupValues(
    pageTitle: string,
    sectionId: string,
    fieldName: string,
    options: any[]
  ): { label: string, checked: boolean }[] {
    const sectionFormGroup = this.formPages[pageTitle].get(sectionId) as FormGroup;
    if (!sectionFormGroup) return [];
    const checkboxGroup = sectionFormGroup.get(fieldName) as FormGroup;
    if (!checkboxGroup) return [];

    return options.map((option) => {
      const control = checkboxGroup.get(option.value);
      const checked = !!control?.value;
      return { label: option.label, checked };
    });
  }

  isOptionChecked(pageTitle: string, sectionId: string, groupName: string, optionValue: string): boolean {
    const sectionFormGroup = this.formPages[pageTitle]?.get(sectionId) as FormGroup;
    if (!sectionFormGroup) return false;

    const checkboxGroup = sectionFormGroup?.get(groupName) as FormGroup;
    if (!checkboxGroup) return false;

    const control = checkboxGroup.get(optionValue);
    return !!control?.value; // true if checked, false if unchecked
  }




  markAllFieldsAsTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup) {
        this.markAllFieldsAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  onSubmit(): void {
    this.loading = true;
    // If ANY step is unsaved, block submission
    const unsavedSteps = this.layout.pages
      .map((page: any, i: number) => ({ title: page.title, saved: this.stepIsSaved[i] }))
      .filter((step: any) => !step.saved);

    if (unsavedSteps.length > 0) {
      // e.g. "Personal Information, Insurance and Medical History"
      const unsavedTitles = unsavedSteps.map((step: any) => step.title).join(', ');

      this.loading = false;
      this.showError(`Error! These pages are unsaved: ${unsavedTitles}`);
      return;
    }
    // Ensure all forms are valid
    const allFormsValid = Object.values(this.formPages).every((fg) => fg.valid);

    if (!allFormsValid) {
      this.loading = false;
      this.showError('Some pages are invalid. Please fix errors before submitting.');
      Object.values(this.formPages).forEach((fg) => this.markAllFieldsAsTouched(fg));
      return;
    }

    // Preprocess phone # across all pages
    Object.values(this.formPages).forEach((fg) => this.preprocessPhoneNumber(fg));

    const submitPayload = {
      form_id: this.formId,
      template_id: this.templateId,
      patient_id: this.patientId,
      client_id: this.clientId,
      submitted_at: new Date().toISOString(),
      submitted_by: this.kioskMode ? 'kiosk' : 'user'
    };

    // Use anonymous method for kiosk mode
    const submitObservable = this.kioskMode
      ? this.formConfigService.submitFormAnonymous(this.clientId, this.patientId, this.formId, submitPayload)
      : this.formConfigService.submitForm(this.clientId, this.patientId, this.formId, submitPayload);

    submitObservable.subscribe({
      next: (response) => {
        this.loading = false;
        this.showError('Form saved successfully.', false);

        // In kiosk mode, emit event instead of navigating
        if (this.kioskMode) {
          this.formSubmitted.emit({
            success: true,
            formId: this.formId,
            templateId: this.templateId
          });
          return;
        }

        // Clear deep link context after successful submission
        this.deeplinkService.clearDeeplinkContext();

        if (this.templateId === 'new_patient_registration') {
          // Remove from pending forms immediately
          const pendingFormsKey = `pending_forms_${this.patientId}`;
          const saved = localStorage.getItem(pendingFormsKey);
          if (saved) {
            let pendingForms = JSON.parse(saved);
            pendingForms = pendingForms.filter((f: string) => f !== 'registration');
            if (pendingForms.length > 0) {
              localStorage.setItem(pendingFormsKey, JSON.stringify(pendingForms));
            } else {
              localStorage.removeItem(pendingFormsKey);
            }
          }
          
          
          // Trigger registration completed event
          this.appStateService.markRegistrationComplete();
          
          // Add a small delay to ensure backend has processed the submission
          setTimeout(() => {
            this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe({
            next: (patientData) => {
              
              // Update the patient data in app state with the latest information
              this.appStateService.setState({ patient: patientData });
              
              // For normal registration, just navigate to dashboard
              // No appointment booking should happen here
              this.router.navigate(['/dashboard/main']);
            },
            error: (error) => {
              this.showError('Error fetching updated patient data.');
              this.router.navigate(['/dashboard/main']);
            }
          });
          }, 1000); // 1 second delay
        } else if (this.templateId === 'informed_consent_general_dentistry') {
          // Remove consent from pending forms immediately
          const pendingFormsKey = `pending_forms_${this.patientId}`;
          const saved = localStorage.getItem(pendingFormsKey);
          if (saved) {
            let pendingForms = JSON.parse(saved);
            pendingForms = pendingForms.filter((f: string) => f !== 'consent');
            if (pendingForms.length > 0) {
              localStorage.setItem(pendingFormsKey, JSON.stringify(pendingForms));
            } else {
              localStorage.removeItem(pendingFormsKey);
            }
          }
          
          // Fetch updated patient data to reflect consent completion
          setTimeout(() => {
            this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe({
              next: (patientData) => {
                
                // Update the patient data in app state
                this.appStateService.setState({ patient: patientData });
                
                this.router.navigate(['/dashboard/main']);
              },
              error: (error) => {
                // Still navigate to dashboard even if fetch fails
                this.router.navigate(['/dashboard/main']);
              }
            });
          }, 1000); // 1 second delay to ensure backend has processed
        } else {
          // Non-registration forms => main dashboard
          // But check if we need to show consent form
          const showConsentAfterRegistration = this.appStateService.getState().showConsentAfterRegistration;
          if (showConsentAfterRegistration && this.formTemplate === 'new_patient_registration') {
            // Clear the flag
            this.appStateService.setState({ showConsentAfterRegistration: false });
            // Navigate to dashboard and show consent popup
            this.router.navigate(['/dashboard/main']).then(() => {
              setTimeout(() => {
                this.showConsentPrompt();
              }, 2000);
            });
          } else {
            this.router.navigate(['/dashboard/main']);
          }
        }
      },
      error: () => {
        this.loading = false;
        this.showError('Error saving form.');
      }
    });

  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard', 'main']);
  }

  showError(message: string, navigate: boolean = false): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000
    });
    if (navigate) {
      this.router.navigate(['/', 'login']);
    }
  }

  onFileChange(event: any, fieldName: string, sectionId: string, pageTitle: string): void {
    const fileInput = event.target;
    const file = fileInput.files?.[0];
    if (!file) {
      this.showError('No file selected. Please choose a file to upload.');
      return;
    }

    // If the same file is chosen again
    if (
      fileInput.files.length > 0 &&
      fileInput.files[0].name === this.fileNames[pageTitle]?.[fieldName]
    ) {
      fileInput.value = '';
      return;
    }

    // Validate file size
    const MAX_FILE_SIZE_MB = 10;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      this.showError(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`);
      fileInput.value = '';
      return;
    }

    // Validate file type
    const supportedTypes = ['image/jpeg', 'image/png'];
    if (!supportedTypes.includes(file.type)) {
      this.showError('Invalid file type. Please upload a JPEG or PNG file.');
      fileInput.value = '';
      return;
    }

    // Check duplicate file (by name + size)
    const fileKey = `${file.name}-${file.size}`;
    if (this.fileNames[pageTitle]?.[fieldName] === fileKey) {
      this.showError('This file has already been uploaded.');
      fileInput.value = '';
      return;
    }

    // Process valid file => compress to base64
    this.resizeAndCompressImage(file, 800, 90)
      .then((compressedBase64) => {
        const formGroup = this.getFormGroup(pageTitle, sectionId);
        if (formGroup) {
          formGroup.patchValue({ [fieldName]: compressedBase64 });

          // Store the file key to prevent duplicates
          this.fileNames[pageTitle] = this.fileNames[pageTitle] || {};
          this.fileNames[pageTitle][fieldName] = fileKey;
        }
      })
      .catch((error) => {
        this.showError('Error processing image. Please try again.');
        fileInput.value = '';
      });
  }

  resizeAndCompressImage(file: File, maxDimension: number, quality: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event: any) => {
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let { width, height } = img;
          // Maintain aspect ratio
          if (width > height) {
            if (width > maxDimension) {
              height *= maxDimension / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width *= maxDimension / height;
              height = maxDimension;
            }
          }
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress and return as base64
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const reader2 = new FileReader();
                reader2.onloadend = () => resolve(reader2.result as string);
                reader2.readAsDataURL(blob);
              } else {
                reject('Compression failed.');
              }
            },
            'image/jpeg', // Force JPEG
            quality / 100 // e.g., 0.9 for 90%
          );
        };
        img.onerror = () => reject('Error loading image.');
      };

      reader.onerror = () => reject('Error reading file.');
      reader.readAsDataURL(file);
    });
  }

  private findFieldConfigForState(pageName: string, controlName: string): any {
    // Look through the form template configuration to find the state field definition
    if (this.formTemplate && this.formTemplate.sections) {
      const section = this.formTemplate.sections[controlName];
      if (section && section.fields) {
        const stateField = section.fields.find((field: any) => field.name === 'state');
        return stateField;
      }
    }
    return null;
  }

  handleAddressChange(address: { street: string; city: string; state: string; stateCode: string; zip: string }): void {
    
    // We'll determine the correct format when we find the actual form field
    
    // Try to find the correct form section
    let formFound = false;
    
    // First try the known pattern
    const basicInfoForm = this.formPages['Personal Information']?.get('basic_information') as FormGroup;
    if (basicInfoForm) {
      // Check if the state field exists and determine what format it expects
      const stateControl = basicInfoForm.get('state');
      let stateValue = address.state; // Default to full name
      
      if (stateControl) {
        // For new patient registration, the basic info likely expects state codes
        // Let's try state code first for registration forms
        if (this.templateId === 'new_patient_registration') {
          stateValue = address.stateCode;
        } else {
          stateValue = address.state;
        }
      }
      
      // For new patient registration, strip ZIP+4 to just 5 digits to match validation pattern '^\d{5}'
      const zipCode = address.zip.includes('-') ? address.zip.split('-')[0] : address.zip;
      
      const updateData = {
        address: address.street,
        city: address.city,
        state: stateValue,
        zipCode: zipCode
      };
      basicInfoForm.patchValue(updateData);
      formFound = true;
    }
    
    // If not found, search through all pages and sections
    if (!formFound) {
      for (const pageName of Object.keys(this.formPages)) {
        const page = this.formPages[pageName];
        if (page) {
          for (const controlName of Object.keys(page.controls)) {
            const control = page.get(controlName);
            if (control instanceof FormGroup) {
              // Check if this section has address fields
              if (control.get('address') || control.get('city') || control.get('state') || control.get('zipCode')) {
                // Determine state format based on section context and field configuration
                let stateValue = address.state; // Default to full name
                
                // Try to detect if this field expects codes or full names
                const stateControl = control.get('state');
                if (stateControl) {
                  
                  // Let's also check the backend form template configuration for this field
                  const fieldConfig = this.findFieldConfigForState(pageName, controlName);
                  
                  if (fieldConfig && fieldConfig.type === 'select' && fieldConfig.options) {
                    // It's a dropdown with predefined options, check if options are codes or full names
                    const firstOption = fieldConfig.options[0]?.value || fieldConfig.options[0];
                    if (firstOption && firstOption.length === 2 && /^[A-Z]{2}$/.test(firstOption)) {
                      // Options are state codes like "AL", "AK", etc.
                      stateValue = address.stateCode;
                    } else {
                      // Options are likely full state names
                      stateValue = address.state;
                    }
                  } else {
                    // Enhanced fallback logic: check page name and control name for context
                    if (pageName.toLowerCase().includes('personal') || pageName.toLowerCase().includes('basic') || 
                        controlName.toLowerCase().includes('basic') || controlName.toLowerCase().includes('personal')) {
                      stateValue = address.state; // Use full state name for personal info sections
                    } else {
                      // For other sections, try to use state codes initially and see if it works
                      stateValue = address.stateCode;
                    }
                  }
                } else {
                  stateValue = address.state; // Fallback to full name if no state field
                }
                
                // Strip ZIP+4 to just 5 digits for form validation
                const zipCode = address.zip.includes('-') ? address.zip.split('-')[0] : address.zip;
                
                const updateData = {
                  address: address.street,
                  city: address.city,
                  state: stateValue,
                  zipCode: zipCode
                };
                
                control.patchValue(updateData);
                formFound = true;
              }
            }
          }
        }
      }
    }
    
    if (!formFound) {
      Object.keys(this.formPages).forEach(pageName => {
        const page = this.formPages[pageName];
      });
    }
  }

  getFormGroup(pageTitle: string, sectionId: string): FormGroup {
    return this.formPages[pageTitle]?.get(sectionId) as FormGroup;
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard', 'main']);
  }

  getCurrentTemplateTitle(): string {
    return this.filteredFormTemplates.length > 0
      ? this.filteredFormTemplates[0].form_title
      : 'Form Selector';
  }


  private showConsentPrompt(): void {
    const dialogRef = this.dialog.open(ConsentPromptComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'fill') {
        // User wants to fill consent form
        this.router.navigate(['/dashboard/consent/informed_consent_general_dentistry']);
      }
      // If skipped, user stays on dashboard
    });
  }
}
